import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ASSIGNEE_FIRST_NAME,
  ASSIGNEE_LAST_NAME,
  ORDER,
  ORDER_BY,
  PATIENT_FIRST_NAME,
  PATIENT_ID,
  PATIENT_LAST_NAME,
  QUEUE_TYPE,
  STATUS,
  WAITING_ROOM_ID,
} from "src/constants/searchParams";
import WaitingRoomTable from "./components/WaitingRoomTable";
import WaitingRoomTrackerFilters from "./components/WaitingRoomTrackerFilters";
import { useGetWaitingRoomsQuery } from "src/api/services/tasksApi";
import { useSelection } from "src/context/ItemsSelectionProvider/useSelection";
import Actions from "./components/WaitingRoomTable/components/Actions";
import ReassignRoomModal from "src/components/Modals/ReassignRoomModal";
import { useManageModal } from "src/hooks/useManageModal";
import { useFromAndTo } from "src/hooks/useFromAndTo";

const WaitingRoomTracker = () => {
  const { selected } = useSelection();
  const [params] = useSearchParams();
  const [openModal, modalActions] = useManageModal();
  const { from, to } = useFromAndTo();

  const queryParams = useMemo(
    () => ({
      patient_first_name: params.get(PATIENT_FIRST_NAME) ?? undefined,
      patient_last_name: params.get(PATIENT_LAST_NAME) ?? undefined,
      patient_id: params.get(PATIENT_ID) ?? undefined,
      id: params.get(WAITING_ROOM_ID) ?? undefined,
      queue: params.get(QUEUE_TYPE) ?? undefined,
      status: params.get(STATUS) ?? undefined,
      assignee_first_name: params.get(ASSIGNEE_FIRST_NAME) ?? undefined,
      assignee_last_name: params.get(ASSIGNEE_LAST_NAME) ?? undefined,
      order_by: params.get(ORDER_BY) ?? undefined,
      ascending: params.get(ORDER) === "asc" ? true : false,
      from,
      to,
    }),
    [from, params, to],
  );

  const { data, isFetching } = useGetWaitingRoomsQuery(queryParams);

  const { waiting_room, count } = useMemo(() => {
    if (data == null) return { waiting_room: [], count: 0 };
    return {
      waiting_room: data.data
        .map((w) => w.waiting_room ?? w)
        .filter((t) => t != null),
      count: data.count,
    };
  }, [data]);

  return (
    <Stack width="100%" gap={3}>
      <Typography variant="h4" fontWeight={600}>
        Waiting Room Tracker
      </Typography>
      <Stack gap={3}>
        {selected.length > 0 ? (
          <Actions handleOpen={modalActions.handleOpen} />
        ) : (
          <WaitingRoomTrackerFilters />
        )}
        <Stack gap={0}>
          <Box height={4}>
            {isFetching ? <LinearProgress color="secondary" /> : null}
          </Box>
          <WaitingRoomTable rooms={waiting_room} total={count} />
        </Stack>
      </Stack>
      <ReassignRoomModal
        open={openModal}
        handleClose={modalActions.handleClose}
      />
    </Stack>
  );
};

export default WaitingRoomTracker;
